<template>
  <div
    ref="rootElement"
    @keydown.esc.exact.prevent="destroyInstance()"
  >
    <FullCalendar
      ref="fullCalendarRef"
      data-cy="calendar-view"
      :style="colorOverrides"
      class="full-calendar-wrapper"
      :options="calendarOptions"
    />
  </div>
</template>

<script setup>
import dayjs from 'dayjs';
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance, h, render } from 'vue';
import { useRoute } from 'vue-router';
import { useNuxtApp } from '#imports';
import FullCalendar from '@fullcalendar/vue3';
import dayGridViewPlugin from '@fullcalendar/daygrid';
import timeGridViewPlugin from '@fullcalendar/timegrid';
import listViewPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import EventPopUp from './EventPopUp';
import EventClockIcon from '../../icons/EventClockIcon.vue';
import ChevronBackIcon from '../../icons/ChevronBackIcon.vue';
import ChevronNextIcon from '../../icons/ChevronNextIcon.vue';
import { hexToRgb, blendAlpha } from '../../../../../helpers/colors';
import { TOOLTIP_POSITIONING, VIEW_TYPES, getSpecificListOrCalView } from '../../helpers/events.helper';
import { useEventsStore } from '../../../../../stores/eventsStore.js';
import { useMainStore } from '../../../../../stores/mainStore.js';

const MOBILE_WIDTH = 600;
const VIEW_TOGGLE_BUTTON_CLASSES = ['fc-dayGridMonth-button', 'fc-timeGridWeek-button'];

defineOptions({ name: 'FullCalendarWrapper' });

const props = defineProps({
  events: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change-view-type']);

const eventsStore = useEventsStore();
const mainStore = useMainStore();
const route = useRoute();
const { $t, $intl } = useNuxtApp();

const vm = getCurrentInstance();
const appContext = vm?.appContext;

const rootElement = ref(null);
const fullCalendarRef = ref(null);
/** @type {import('vue').Ref<import('@fullcalendar/core').CalendarApi | null>} */
const fullCalendarApi = ref(null);
const viewType = ref(VIEW_TYPES.MONTH);
const childInstance = ref(null);
const originalRef = ref(null);

const lastActiveEndDate = computed(() => eventsStore.lastActiveEndDate);
const queryParams = computed(() => eventsStore.queryParams);
const selectedYearUpdated = computed(() => eventsStore.selectedYearUpdated);
const calendarViewType = computed(() => eventsStore.calendarViewType);
const globals = computed(() => mainStore.globals);
const customSections = computed(() => mainStore.customSections);
const headingColor = computed(() => mainStore.getThemeHeadingColor);
const year = computed(() => eventsStore.getCurrentYear);
const date = computed(() => eventsStore.getCurrentDate);

// Week labels read "October-5-2026 - October-11-2026"; `endDate` is exclusive, as FullCalendar and the query params give it.
function buildNavigationLabels(viewTypeName, startDate, endDate) {
  if (viewTypeName === VIEW_TYPES.WEEK) {
    const accessibleWeek = `${dayjs(startDate).format('MMMM-D-YYYY')} - ${dayjs(endDate).subtract(1, 'day').format('MMMM-D-YYYY')}`;
    return {
      previous: $t('events.previousWeek', { accessibleWeek }),
      next: $t('events.nextWeek', { accessibleWeek }),
    };
  }
  const accessibleMonth = dayjs(startDate).format('MMMM');
  return {
    previous: $t('events.previousMonth', { accessibleMonth }),
    next: $t('events.nextMonth', { accessibleMonth }),
  };
}

const navigationLabels = computed(() =>
  buildNavigationLabels(viewType.value, queryParams.value.start_date, queryParams.value.end_date),
);
const previousButtonLabel = computed(() => navigationLabels.value.previous);
const nextButtonLabel = computed(() => navigationLabels.value.next);

const colorOverrides = computed(() => {
  const color = headingColor.value;
  if (!color) {
    return {};
  }

  const targetRGB = { ...hexToRgb(color), a: 0.1 };
  const backgroundRGB = blendAlpha({ ...hexToRgb(color), a: 0.08 });

  return {
    '--button-outline': color,
    '--button-primary-bg': color,
    '--button-primary-bg-hover': color,
    '--table-border': `${color}1A`,
    '--event-bg': `${blendAlpha(targetRGB, true, backgroundRGB)}`,
    '--today-highlight-color': `${color}14`,
    '--month-week-border-color': `${color}4D`,
    '--month-week-bg-active': `${color}1A`,
    '--border-events': `1px solid ${color}`,
    '--month-week-width': viewType.value === VIEW_TYPES.WEEK ? '315px' : '215px',
  };
});

function getFollowingMonth(currStartYear, startMonth) {
  return {
    startDate: new Date(currStartYear, startMonth + 1, 1).toISOString().split('T')[0],
    endDate: new Date(currStartYear, startMonth + 2, 0).toISOString().split('T')[0],
  };
}

function eventTags(event) {
  const tags = [];
  const section = customSections.value.find((sec) => sec.id === event.custom_section_id);
  if (section) tags.push(section.name);
  tags.push(...event.filter_name);
  return tags;
}

function destroyInstance() {
  if (originalRef.value) {
    render(null, originalRef.value);
    childInstance.value = null;
    originalRef.value = null;
  }
}

function createSVGClockInstance() {
  const instance = h(EventClockIcon);
  if (appContext) instance.appContext = appContext;
  return instance;
}

function createSVGChevronInstance(useNextIcon = false) {
  const instance = h(useNextIcon ? ChevronNextIcon : ChevronBackIcon);
  if (appContext) instance.appContext = appContext;
  return instance;
}

function todayButtonAction() {
  const todaysDate = new Date();
  const payload = {
    start: todaysDate,
    end: todaysDate,
    view: {
      currentStart: todaysDate,
      currentEnd: todaysDate,
    },
  };
  handleChangeDate(payload, true);
}

// FullCalendar marks the prev/next chevrons `role="img"` with no name, gives icon-only buttons a `title` and no
// `aria-label`, and puts `aria-pressed` on buttons that are not toggles. Hide the icons, name the chevron buttons
// explicitly, and keep `aria-pressed` only on the Month/Week toggle.
function fixToolbarAccessibility(labels = { previous: previousButtonLabel.value, next: nextButtonLabel.value }) {
  const toolbarElement = rootElement.value?.querySelector('.fc-header-toolbar');
  if (!toolbarElement) return;

  // Labels come from our own values rather than the rendered `title`, which lags a FullCalendar re-render.
  toolbarElement.querySelector('.fc-customLeft-button')?.setAttribute('aria-label', labels.previous);
  toolbarElement.querySelector('.fc-customRight-button')?.setAttribute('aria-label', labels.next);

  toolbarElement.querySelectorAll('.fc-icon').forEach((iconElement) => {
    iconElement.removeAttribute('role');
    iconElement.setAttribute('aria-hidden', 'true');
    iconElement.tabIndex = -1;
  });

  toolbarElement.querySelectorAll('.fc-button[aria-pressed]').forEach((buttonElement) => {
    const isViewToggle = VIEW_TOGGLE_BUTTON_CLASSES.some((className) => buttonElement.classList.contains(className));
    if (!isViewToggle) buttonElement.removeAttribute('aria-pressed');
  });
}

function handleDatesSet(payload) {
  const result = handleChangeDate(payload);
  // `queryParams` only catches up once the events fetch finishes, so name the buttons from the range FullCalendar
  // just rendered. The watcher below re-applies them from `queryParams` afterwards.
  const renderedViewType = payload.view?.type ?? viewType.value;
  const labels =
    renderedViewType === VIEW_TYPES.WEEK
      ? buildNavigationLabels(renderedViewType, payload.start, payload.end)
      : buildNavigationLabels(renderedViewType, payload.view.currentStart);
  nextTick(() => fixToolbarAccessibility(labels));
  return result;
}

function dateChanged(payload) {
  if (viewType.value === 'timeGridWeek') {
    return date.value.toISOString() !== payload.start.toISOString();
  }
  return date.value.getMonth() !== payload.view.currentStart.getMonth();
}

function handleChangeDate(payload, todayAction) {
  eventsStore.setCalViewType(viewType.value);
  if (dateChanged(payload) || todayAction) {
    eventsStore.setLastActiveEndDate(payload.view.activeEnd);
    const dayNum = payload.view.currentStart.getDate();
    const startMonth = payload.view.currentStart.getMonth();
    const endMonth = payload.view.currentStart.getMonth() + 1;
    let currStartYear = payload.view.currentStart.getFullYear();
    let startDate = new Date(currStartYear, startMonth, 1).toISOString().split('T')[0];
    let endDate = new Date(currStartYear, endMonth, 0).toISOString().split('T')[0];
    if (viewType.value === 'timeGridWeek') {
      if (selectedYearUpdated.value) {
        eventsStore.setSelectedYearUpdated(false);
        currStartYear = year.value;
      }
      startDate = new Date(currStartYear, payload.start.getMonth(), payload.start.getDate())
        .toISOString()
        .split('T')[0];
      endDate = new Date(payload.end.getFullYear(), payload.end.getMonth(), payload.end.getDate())
        .toISOString()
        .split('T')[0];
    }
    eventsStore.setCurrentDate({
      year: currStartYear,
      month: startMonth,
      day: dayNum,
    });
    return navigateTo({
      query: {
        ...route.query,
        ...queryParams.value,
        start_date: startDate,
        end_date: endDate,
        date: undefined,
        id: undefined,
        view: getSpecificListOrCalView(eventsStore),
      },
    });
  }
}

function handleEventClick($event) {
  const { jsEvent, event, el } = $event;
  jsEvent.preventDefault();
  jsEvent.stopPropagation();

  if (childInstance.value && event.extendedProps.id === childInstance.value.props?.event?.id) {
    destroyInstance();
    return;
  }

  if (childInstance.value) {
    destroyInstance();
  }

  const eventData = event.extendedProps;

  let windowWidth = null;
  if (import.meta.client) {
    windowWidth = window.innerWidth;
  }

  if (windowWidth < MOBILE_WIDTH) {
    return;
  }
  createNewInstance(eventData, el);
}

function createNewInstance(event, el) {
  const elRoot = rootElement.value;
  if (!elRoot) return;

  const rect = elRoot.getBoundingClientRect();
  const parentBoundingBox = {
    element: elRoot,
    top: rect.top + window.scrollY,
    bottom: rect.bottom + window.scrollY,
    left: rect.left + window.scrollX,
    right: rect.right + window.scrollX,
    width: rect.width,
    height: rect.height,
    x: rect.x + window.scrollX,
    y: rect.y + window.scrollY,
  };
  const elementBounding = el.getBoundingClientRect();
  const originalRefNode = viewType.value === VIEW_TYPES.WEEK ? el.parentNode.parentNode : el.parentNode;
  const instance = h(EventPopUp, {
    event: {
      ...event,
      eventTags: eventTags(event),
      type: 'event',
    },
    getGlobals: globals.value,
    tooltipPosition: TOOLTIP_POSITIONING(el.parentNode, elRoot, viewType.value),
    parentBoundingBox: parentBoundingBox,
    colorOverrides: colorOverrides.value,
    eventYPos: elementBounding.top + elementBounding.height / 2,
    viewType: calendarViewType.value,
    onClickedAway: () => destroyInstance(),
    onCloseEsc: () => el.focus(),
    onViewEvent: ($event) => viewEvent($event),
  });
  if (appContext) instance.appContext = appContext;
  childInstance.value = instance;
  originalRef.value = originalRefNode;
  render(instance, originalRefNode);
}

function viewEvent(id) {
  eventsStore.changeEventView('LIST_VIEW');
  return navigateTo({
    query: {
      ...route.query,
      view: undefined,
      id,
    },
  });
}

function handleViewDidMount({ view }) {
  if (viewType.value === view.type) return;

  viewType.value = view.type;
  eventsStore.setCalViewType(viewType.value);

  if (fullCalendarApi.value) {
    fullCalendarApi.value.setOption('height', view.type === 'dayGridMonth' ? null : 'auto');
  }
  emit('change-view-type', view.type);
  if (view.type === 'dayGridMonth') {
    const dayNum = view.currentStart.getDate();
    const startMonth = view.currentStart.getMonth();
    let endMonth = view.activeEnd.getMonth();
    const currStartYear = view.currentStart.getFullYear();

    if (lastActiveEndDate.value) {
      endMonth = lastActiveEndDate.value.getMonth();
    }

    const rangeDates =
      endMonth > startMonth
        ? getFollowingMonth(currStartYear, startMonth)
        : {
            startDate: view.currentStart.toISOString().split('T')[0],
            endDate: view.currentEnd.toISOString().split('T')[0],
          };

    eventsStore.setCurrentDate({
      year: currStartYear,
      month: startMonth,
      day: dayNum,
    });
    return navigateTo({
      query: {
        ...route.query,
        ...queryParams.value,
        start_date: rangeDates.startDate,
        end_date: rangeDates.endDate,
        date: undefined,
        id: undefined,
        view: getSpecificListOrCalView(eventsStore),
      },
    });
  }
}

const calendarOptions = computed(() => ({
  locale: $intl.locale,
  plugins: [dayGridViewPlugin, timeGridViewPlugin, listViewPlugin, interactionPlugin],
  initialView: calendarViewType.value || 'dayGridMonth',
  headerToolbar: {
    left: 'customLeft,title,customRight',
    center: '',
    right: 'customToday dayGridMonth,timeGridWeek',
  },
  buttonText: {
    today: $t('events.today'),
  },
  events: props.events,
  eventOrder: 'start_at',
  eventClick: handleEventClick,
  viewDidMount: handleViewDidMount,
  datesSet: handleDatesSet,
  views: {
    dayGridMonth: {
      buttonText: $t('events.month'),
    },
    timeGridWeek: {
      buttonText: $t('events.week'),
      allDaySlot: true,
      allDayText: $t('events.allDayUppercase'),
      slotMinWidth: '500px',
    },
  },
  customButtons: {
    customToday: {
      text: 'Today',
      click: () => {
        todayButtonAction();
      },
    },
    customLeft: {
      text: previousButtonLabel.value,
      icon: 'chevron-left',
      click: () => {
        fullCalendarApi.value?.prev();
      },
    },
    customRight: {
      text: nextButtonLabel.value,
      icon: 'chevron-right',
      click: () => {
        fullCalendarApi.value?.next();
      },
    },
  },
  height: 'auto',
  initialDate: date.value,
  eventDidMount: (info) => {
    const { el, event, view } = info;
    el.href = 'javascript:void(0);';
    el.onfocus = destroyInstance;
    if (view.type === VIEW_TYPES.WEEK) {
      const { formatted_start, formatted_end, all_day } = event.extendedProps;
      const start = dayjs(formatted_start);
      const end = dayjs(formatted_end);
      const isMultiDay = start.date() !== end.date();
      const eventTitleNode = el.querySelector('.fc-event-title');
      if (isMultiDay && !all_day) {
        const clockIconInstance = createSVGClockInstance();
        render(clockIconInstance, eventTitleNode);
      }

      if (event.allDay) {
        const viewStartDate = dayjs(view.currentStart);
        const viewEndDate = dayjs(view.currentEnd);

        if (start.isBefore(viewStartDate)) {
          const chevronIconInstance = createSVGChevronInstance(false);
          render(chevronIconInstance, eventTitleNode);
        }

        if (end.isAfter(viewEndDate)) {
          const chevronIconInstance = createSVGChevronInstance(true);
          render(chevronIconInstance, eventTitleNode);
        }
      }
    }
  },
  allDayContent: '',
}));

// The visible month only updates after the events fetch completes, so re-apply the labels when they change.
watch([previousButtonLabel, nextButtonLabel], () => fixToolbarAccessibility(), { flush: 'post' });

onMounted(() => {
  fullCalendarApi.value = fullCalendarRef.value?.getApi?.() ?? null;
  fixToolbarAccessibility();
});
</script>

<style lang="scss" scoped>
:deep(.fc) {
  .fc-scrollgrid-section-sticky > * {
    z-index: auto;
  }
  .fc-timegrid-event-harness {
    .fc-timegrid-event {
      min-height: 24px;
    }
  }
  .fc-event {
    .fc-event-title-container {
      flex-grow: 0;
      flex-shrink: 0;
    }
    .fc-event-main {
      background-color: var(--event-bg);

      .fc-event-main-frame {
        flex-direction: column-reverse;
        justify-content: flex-end;
        overflow: hidden;
      }
    }
  }
  .fc-scroller-harness,
  .fc-scroller-liquid-absolute,
  .fc-scroller {
    overflow: visible !important;
  }
  .fc-day:has(.cal-event-popup) {
    z-index: 99;
    position: relative;
  }
}

@mixin cal-button-override {
  background: transparent;
  border: none;
}

@mixin cal-font-override {
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;

  display: flex;
  align-items: center;
  text-align: center;
  text-transform: capitalize;
}

.view-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;

  .view-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 24px;

    .view-header-left,
    .view-header-right {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
  }

  .full-calendar-wrapper {
    flex: 1;
  }
}

.full-calendar-wrapper {
  margin-top: 32px;

  & :deep() {
    --fc-event-text-color: #333;
    --fc-event-border-color: var(--event-bg);
    --fc-event-bg-color: var(--event-bg);

    --fc-today-bg-color: var(--today-highlight-color);

    --fc-border-color: var(--table-border);
    --fc-button-active-border-color: var(--month-week-border-color);

    .fc-dayGridMonth-view {
      list-style: none;
    }
    & .fc-view-harness {
      min-height: 300px;
    }

    & .fc-timegrid-axis-cushion {
      max-width: none;
      min-width: 100px;
      text-align: center;
    }

    .fc-day-other {
      .fc-daygrid-day-top {
        opacity: 0.6;
      }
    }

    & .fc-daygrid-day-top {
      flex-direction: initial;
      padding-left: 8px;
      font-weight: 500;
    }

    & .fc-col-header-cell-cushion {
      font-weight: 400;
    }

    & .fc-daygrid {
      & .fc-col-header-cell-cushion {
        float: right;
      }
    }

    & .fc-timegrid-slot-label-frame {
      text-align: center;
      text-transform: uppercase;
    }

    & .fc-header-toolbar {
      min-height: 90px;
      padding: 24px;
      border: 1px solid rgba(0, 73, 144, 0.1);
      border-radius: 3px;
      margin: 0;
      flex-wrap: wrap;
      gap: 16px 8px;
    }

    & .fc-toolbar-chunk {
      &:first-of-type {
        & div {
          flex: 1;
          display: flex;
          align-items: center;
        }

        & > * {
          & > .fc-toolbar-title {
            font-style: normal;
            font-weight: 500;
            font-size: 24px;
            line-height: 23px;
            width: var(--month-week-width);
            min-width: 0;
            max-width: 100%;
            text-align: center;
            color: #333333;
          }

          & > .fc-button {
            @include cal-button-override;
            color: #00376c; /* TODO: Should be Dynamic*/
          }
        }
      }
      &:last-of-type {
        display: flex;
        & > .fc-button {
          @include cal-button-override;
          @include cal-font-override;
          border: 1px solid rgba(0, 73, 144, 0.3);
          border-radius: 4px;
          color: #333333;
          padding: 12px;
          border: 1px solid var(--month-week-border-color);
        }

        & > .fc-button-group {
          border: 1px solid rgba(0, 73, 144, 0.3);
          border-radius: 4px;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 8px;
          margin-left: 8px;
          border: 1px solid var(--month-week-border-color);
        }

        & > * {
          & > .fc-button {
            @include cal-button-override;
            @include cal-font-override;
            color: #333333;

            border-radius: 4px;
            height: 20px;
            &.fc-button-active {
              background: var(--month-week-bg-active);
            }
          }
        }
      }
    }
    & .fc-button.fc-button-primary {
      &.fc-customLeft-button,
      &.fc-customRight-button,
      &.fc-customToday-button,
      &.fc-dayGridMonth-button,
      &.fc-timeGridWeek-button {
        &:focus {
          box-shadow: none;
        }
        &:focus-visible {
          outline: 2px solid #333333;
          outline-offset: 2px;
          /* White halo fills the outline gap so the dark ring keeps 3:1 on any themed background. */
          box-shadow: 0 0 0 2px #ffffff;
        }
      }
    }
    &
      > .fc-view-harness
      > div
      > table
      > tbody
      > tr
      > td
      > div
      > div
      > div
      > table
      > tbody
      > tr
      > td.fc-daygrid-day
      > div
      > div.fc-daygrid-day-events
      > div {
      cursor: pointer;

      & > a > div.fc-event-main > div.fc-event-main-frame > div.fc-event-title-container > div.fc-event-title {
        &::marker {
          margin-right: 8px;
        }

        display: list-item;
        list-style-position: inside;
        margin-left: 8px;
      }
    }
  }
}
</style>
